import { useEffect } from 'react';

/**
 * useScrollReveal — sitewide entrance animations.
 *
 * Modern 2026 patterns:
 *   - One-shot fade + scale + de-blur (depth-of-field focus) on [data-reveal]
 *   - Staggered children on [data-reveal-stagger]
 *   - Character-by-character split + reveal on [data-char-reveal]
 *   - 3D mouse-tracked tilt on .tilt3d
 *   - Magnetic cursor follow on .magnetic
 *   - Counter roll-up on [data-counter-to]
 *
 * One IntersectionObserver drives all entrance animations. Listeners
 * stay attached for the lifetime of the page; MutationObserver picks up
 * client-side-routed content.
 */
export function useScrollReveal() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ── 1. Character split: wrap each non-space char in a <span class="char">
    const splitOnce = (el) => {
      if (el.dataset.charSplit === 'done') return;
      const text = el.textContent || '';
      el.textContent = '';
      for (const ch of text) {
        if (ch === ' ') {
          el.appendChild(document.createTextNode(' '));
        } else {
          const s = document.createElement('span');
          s.className = 'char';
          s.textContent = ch;
          el.appendChild(s);
        }
      }
      el.classList.add('char-reveal');
      el.dataset.charSplit = 'done';
    };
    document.querySelectorAll('[data-char-reveal]').forEach(splitOnce);

    // ── 2. Entrance reveal observer (one-shot)
    const revealSelector = '[data-reveal], [data-reveal-stagger], [data-char-reveal]';
    const reveal = (el) => el.classList.add('is-revealed');

    if (reduced) {
      document.querySelectorAll(revealSelector).forEach(reveal);
    }

    const io = !reduced
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) {
                reveal(e.target);
                io.unobserve(e.target);
              }
            });
          },
          // Fire BEFORE the section reaches the viewport. A positive
          // bottom rootMargin extends the IO root downward by 200px, so
          // a section starts fading-in while it's still 200px below the
          // fold. Combined with the shorter 180ms transition this means
          // the fade has fully completed by the time the user actually
          // scrolls to the section — eliminating the "fade catching up
          // to my scroll" lag the user reported. Threshold lowered to
          // 0.01 so a single visible pixel triggers the reveal.
          { threshold: 0.01, rootMargin: '0px 0px 200px 0px' }
        )
      : null;

    const observeAll = () => {
      if (!io) return;
      document.querySelectorAll(revealSelector).forEach((el) => {
        if (!el.classList.contains('is-revealed')) io.observe(el);
      });
      document.querySelectorAll('[data-char-reveal]').forEach(splitOnce);
    };
    observeAll();

    // ── 3. 3D mouse-tracked tilt on .tilt3d
    const tiltCleanup = [];
    document.querySelectorAll('.tilt3d').forEach((el) => {
      if (reduced) return;
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = `perspective(900px) rotateX(${(-y * 8).toFixed(2)}deg) rotateY(${(x * 10).toFixed(2)}deg)`;
        el.classList.add('is-tilting');
      };
      const onLeave = () => {
        el.style.transform = '';
        el.classList.remove('is-tilting');
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      tiltCleanup.push(() => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      });
    });

    // ── 4. Magnetic cursor on .magnetic
    const magCleanup = [];
    document.querySelectorAll('.magnetic').forEach((el) => {
      if (reduced) return;
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - (r.left + r.width / 2);
        const y = e.clientY - (r.top + r.height / 2);
        el.style.transform = `translate3d(${(x * 0.18).toFixed(1)}px, ${(y * 0.18).toFixed(1)}px, 0)`;
      };
      const onLeave = () => { el.style.transform = ''; };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      magCleanup.push(() => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      });
    });

    // ── 5. Counter roll-up on [data-counter-to]
    const countObs = !reduced
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              const el = e.target;
              const target = parseInt(el.dataset.counterTo || '0', 10);
              const dur = parseInt(el.dataset.counterDur || '1400', 10);
              const start = performance.now();
              const startVal = 0;
              const tick = (t) => {
                const p = Math.min(1, (t - start) / dur);
                const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
                el.textContent = Math.round(startVal + (target - startVal) * eased).toLocaleString();
                if (p < 1) requestAnimationFrame(tick);
              };
              requestAnimationFrame(tick);
              countObs.unobserve(el);
            });
          },
          { threshold: 0.4 }
        )
      : null;
    document.querySelectorAll('[data-counter-to]').forEach((el) => {
      if (countObs) countObs.observe(el);
    });

    // Re-scan when client-side route changes inject new content
    const mo = new MutationObserver(() => {
      observeAll();
      document.querySelectorAll('[data-counter-to]:not([data-counter-done])').forEach((el) => {
        if (countObs) countObs.observe(el);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (io) io.disconnect();
      if (countObs) countObs.disconnect();
      mo.disconnect();
      tiltCleanup.forEach((fn) => fn());
      magCleanup.forEach((fn) => fn());
    };
  }, []);
}

export default useScrollReveal;
